import * as BABYLON from 'babylonjs';
import * as BABYLONGUI from 'babylonjs-gui';
import XmlLoader2 from './XmlLoader2';
import * as Interfaces from '../../Interfaces/Interfaces';
import * as Inovx from '@shared';
import BoxManager from '../viewer/BoxManager';
import PanoEvents from '../Enumerations/ViewerEnums';
import * as Enumerations from '../../Enumerations/Enumerations';
import ExamineCamera from '../Cameras/ExamineCamera';
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

/*
interface ILabel {
    labelControl: BABYLONGUI.Container,
    attachedMesh: BABYLON.Mesh,
    line?: BABYLON.LinesMesh,
    lineData?: { color: string },
    annotationData: IAnnotationDisplayItem
}
*/
/*

interface ILabel {
    labelControl: BABYLONGUI.Container,
    attachedMesh: BABYLON.Mesh,
    leaderLine?: BABYLONGUI.Line,
    lineData?: { color: string },
    annotationData: IAnnotationDisplayItem
}
*/
interface ILabel {
    labelControl: BABYLONGUI.Container;
    attachedMesh: BABYLON.Mesh;
    leaderLine?: BABYLONGUI.Line;
    lineData?: { color: string };
    annotationData: IAnnotationDisplayItem;

    // cache
    lastMeasure?: { left: number; top: number; width: number; height: number };
}
class LabelManager implements Interfaces.Interfaces.ILabelManager {
    private _labelEventManager: Interfaces.Interfaces.IEventManager;
    private readonly SelectedTagColor = "#00c6cd";        
    private readonly SelectedFontColor = "#ece9e9";       
    private _tagsVisible: boolean = false;
    private _showOccludedTags: boolean = false;
    private _scene: BABYLON.Scene;
    private _guiTexture: BABYLONGUI.AdvancedDynamicTexture;
    private _labels: ILabel[] = [];
    private _selectedLabelIds: Set<string> = new Set<string>();
    private _preSelectCache: Map<string, {color: string, background: string, textMap: Map<BABYLONGUI.TextBlock, string>}> = new Map();
    private _preColorizeCache: Map<string, {color: string, background: string, textMap: Map<BABYLONGUI.TextBlock, string>}> = new Map();
    private _activeAppearance: Map<string, { backgroundHex: string, textColor: string }> = new Map();

    private _occlCursor = 0;
    private readonly OccludedTagAlpha = 0.55;
    private readonly OccludedTagLineAlpha = "99";

    private _boxManager: BoxManager;
    public events: EventTarget;

    constructor(scene: BABYLON.Scene, labelEventManager: Interfaces.Interfaces.IEventManager) {
        this._scene = scene;
        // Scene mode BoxManager (multi-box)
        this._boxManager = new BoxManager({ kind: 'scene', scene });

        this.events = new EventTarget();
        this._labelEventManager = labelEventManager;

        // Listen for per-box changes (detail.id tells which annotation)
        this._boxManager.events.addEventListener(PanoEvents.BoxManager.BOX_CHANGED, ((ev: any) => {
            const changedId: string | undefined = ev?.detail?.id;
            if (!changedId) return;

            const label = this._labels.find(l => l.annotationData.ID === changedId);
            if (!label) return;

            const box = this._boxManager.getBoxProperties(changedId);
            if (!box) return;

            const half = new BABYLON.Vector3(box.xDim / 2, box.yDim / 2, box.zDim / 2);
            const boxMin = box.realWorldPos.subtract(half);
            const boxMax = box.realWorldPos.add(half);

            const annotation = label.annotationData;
            annotation.TargetBoxMin = { X: boxMin.x, Y: boxMin.y, Z: boxMin.z };
            annotation.TargetBoxMax = { X: boxMax.x, Y: boxMax.y, Z: boxMax.z };
            annotation.Target3D = { X: box.realWorldPos.x, Y: box.realWorldPos.y, Z: box.realWorldPos.z };

            label.attachedMesh.position.copyFrom(box.realWorldPos);

            this.precomputeAnnotationLayouts();
            this._refreshLeaderLineNow(label);

            this._labelEventManager.fireEvent(Enumerations.EventType.VOLUME_TAG_CHANGED, annotation);
        }) as EventListener);

        this._scene.onAfterCameraRenderObservable.add((camera) => this._onAfterCameraRender(camera));
    }

    public dispose() {
        // (add cleanup if you own the BoxManager lifecycle)
    }

    public setPositioningMode(mode: "move" | "scale" | "none") {
        // Delegates to currently selected box
        this._boxManager.setPositioningMode(mode);
    }

    public getLabelData(annotation: string) {
        return this._labels.find(label => label.annotationData.ID === annotation);
    }

    public getSelectedAnnotationIds(): string[] {
        return Array.from(this._selectedLabelIds);
    }

    public clearEditingGizmos(): void {
        this._boxManager.clearEditingGizmos();
    }

    public clearSelectedAnnotations(): void {
        const selectedIds = Array.from(this._selectedLabelIds);
        for (const id of selectedIds) {
            const label = this._labels.find(l => l.annotationData.ID === id);
            if (label) {
                this.selectLabel(label, false);
            }
        }

        this._selectedLabelIds.clear();
        this._boxManager.setPositioningMode('none');
    }

    private _getStableMeasure(label: ILabel) {
        const m = (label.labelControl as any)?._currentMeasure as BABYLONGUI.Measure | undefined;

        const valid =
            !!m &&
            !(m.left == 0 && m.top == 0) &&
            m.width > 0 && m.height > 0;

        if (valid) {
            label.lastMeasure = { left: m.left, top: m.top, width: m.width, height: m.height };
            return label.lastMeasure;
        }

        return label.lastMeasure ?? null;
    }

    public isMeshAttachmentSphere(mesh: BABYLON.Mesh): boolean { 
        var isAnnotationSphere =
            !!mesh &&
            !!mesh.metadata?._isAnnotationSphere;

        return isAnnotationSphere;
    }
    
    public initializeAnnotations(annotations: IAnnotationDisplayItem[]) {

        if (!this._guiTexture) {
            this._guiTexture = BABYLONGUI.AdvancedDynamicTexture.CreateFullscreenUI('UI');
        }

        // ---- Phase 0: prepare lookups ----
        // existingById: id -> ILabel
        const existingById: { [id: string]: ILabel } = {};
        for (let i = 0; i < this._labels.length; i++) {
            const lbl = this._labels[i];
            existingById[lbl.annotationData.ID] = lbl;
        }

        // incomingIds set
        const incomingIds: { [id: string]: true } = {};
        for (let i = 0; i < annotations.length; i++) {
            incomingIds[annotations[i].ID] = true;
        }

        const nextLabels: ILabel[] = [];

        // ---- Phase 1: upsert & rebuild GUI (but DO NOT remove boxes for existing IDs) ----
        for (let i = 0; i < annotations.length; i++) {
            const annotation = annotations[i];
            const id = annotation.ID;
            const existing = existingById[id];
            let labelData = existing;
            
            const needsRebuild =
                !existing ||
                this.hasAnnotationChanged(existing.annotationData, annotation);
/*
            const needsRebuild =
                !existing ||
                this.hasAnnotationChanged(existing.annotationData, annotation);
*/
            if (needsRebuild) {
                const prevLastMeasure = existing?.lastMeasure;
                const wasSelected = !!existing && this._selectedLabelIds.has(id);

                if (existing) this._deleteLabelUIOnly(existing, true);

                labelData = this.createAnnotation(annotation, annotation.AssetClass.TagDisplayData);

                // restore cached measure (selected or not)
                if (prevLastMeasure) labelData.lastMeasure = prevLastMeasure;

                if (wasSelected) {
                    labelData.annotationData = { ...annotation };
                    this.selectLabel(labelData, true);
                    this.precomputeAnnotationLayouts();
                    this._refreshLeaderLineNow(labelData);
                }

                const isAssetTag =
                    annotation.AnnotationType === AnnotationType.AssetTag ||
                    annotation.AnnotationType === AnnotationType.AssetTagWithBox;

                const isAlwaysVisible =
                    annotation.AnnotationType === AnnotationType.ObservationTag ||
                    annotation.AnnotationType === AnnotationType.LinkAnnotation ||
                    annotation.AnnotationType === AnnotationType.InformationAnnotation;

                const tagVisible = (isAssetTag && this._tagsVisible) || isAlwaysVisible;

                this.applyLabelVisibility(labelData!, tagVisible);
                
            }

            // Always ensure a box exists & is up to date (boxes always-on)
            if (annotation.TargetBoxMin) {
                const center = new BABYLON.Vector3(
                    (annotation.TargetBoxMin.X + annotation.TargetBoxMax.X) / 2,
                    (annotation.TargetBoxMin.Y + annotation.TargetBoxMax.Y) / 2,
                    (annotation.TargetBoxMin.Z + annotation.TargetBoxMax.Z) / 2
                );
                const size = new BABYLON.Vector3(
                    annotation.TargetBoxMax.X - annotation.TargetBoxMin.X,
                    annotation.TargetBoxMax.Y - annotation.TargetBoxMin.Y,
                    annotation.TargetBoxMax.Z - annotation.TargetBoxMin.Z
                );

                this._boxManager.upsertBox(id, {
                    realWorldPos: center,
                    xDim: size.x, // depth -> world Z
                    yDim: size.y, // height -> world Y
                    zDim: size.z  // width  -> world X
                });

                // Visibility should mirror label visibility
                this._boxManager.setBoxVisibility(id, this._tagsVisible);
            }

            if (labelData!) nextLabels.push(labelData!);
        }

        // ---- Phase 2: remove labels (and boxes) that are no longer present ----
        for (let i = 0; i < this._labels.length; i++) {
            const lbl = this._labels[i];
            const id = lbl.annotationData.ID;
            if (!incomingIds[id]) {
                // Safe to remove everything (GUI + box) for vanished IDs
                this.deleteLabel(lbl); // <- removeBox defaults to true below
            }
        }

        // Swap label list
        this._labels = nextLabels;

        // Prevent initial flicker
        this.precomputeAnnotationLayouts();
    }

    private _refreshLeaderLineNow(labelData: ILabel) {
        const line = labelData.leaderLine;
        if (!line) return;

        const scene = this._scene;
        const engine = scene.getEngine();
        const cam = scene.activeCamera;
        if (!cam || !this._guiTexture) return;

        // If label isn't visible, don't bother (and avoids junk measures)
        if (!labelData.labelControl?.isVisible) return;

        const renderW = engine.getRenderWidth();
        const renderH = engine.getRenderHeight();
        const vp = cam.viewport.toGlobal(renderW, renderH);

        // 3D anchor -> screen
        const p3 = labelData.attachedMesh.getAbsolutePosition();
        const projected = BABYLON.Vector3.Project(
            p3,
            BABYLON.Matrix.Identity(),
            scene.getTransformMatrix(),
            vp
        );

        // behind camera?
        if (projected.z < 0 || projected.z > 1) return;

        // measure (your stable getter also caches)
        const rect = this._getStableMeasure(labelData);
        if (!rect) return;

        const labelGuiX = rect.left + rect.width * 0.5;
        const labelGuiY = rect.top;

        // viewport px -> GUI px
        const guiSize = this._guiTexture.getSize();
        const anchorGuiX = ((projected.x - vp.x) / vp.width) * guiSize.width;
        const anchorGuiY = ((projected.y - vp.y) / vp.height) * guiSize.height;

        line.x1 = anchorGuiX;
        line.y1 = anchorGuiY;
        line.x2 = labelGuiX;
        line.y2 = labelGuiY;
    }

    private _isBoxLabel(label: any) {
        return label.annotationData.TargetBoxMin;
    }

    private _setTextColorRecursive(root: BABYLONGUI.Control, color: string, cache: Map<BABYLONGUI.TextBlock, string>): void {
        if (!root) return;

        if (root instanceof BABYLONGUI.TextBlock) {
            if (!cache.has(root)) {
                cache.set(root, (root as any)._annotationBaseColor ?? root.color);
            }
            root.color = color;
        }

        const anyCtrl = root as any;
        if (anyCtrl.children && Array.isArray(anyCtrl.children)) {
            for (const child of anyCtrl.children as BABYLONGUI.Control[]) {
                this._setTextColorRecursive(child, color, cache);
            }
        }
    }

    private _captureBaseTextColors(root: BABYLONGUI.Control): void {
        if (!root) return;

        if (root instanceof BABYLONGUI.TextBlock) {
            (root as any)._annotationBaseColor = root.color;
        }

        const anyCtrl = root as any;
        if (anyCtrl.children && Array.isArray(anyCtrl.children)) {
            for (const child of anyCtrl.children as BABYLONGUI.Control[]) {
                this._captureBaseTextColors(child);
            }
        }
    }

    private _restoreTextColors(cache: Map<BABYLONGUI.TextBlock, string> | null | undefined): void {
        if (!cache) return;
        cache.forEach((original, tb) => {
            try {
                // guard in case control was disposed
                if (tb && !(tb as any).isDisposed?.()) {
                    (tb as any).color = original;
                }
            } catch { /* noop */ }
        });
    }

    private hasAnnotationChanged(annotation: IAnnotationDisplayItem, updatedAnnotation: IAnnotationDisplayItem): boolean {
        var c =  annotation?.Text != updatedAnnotation?.Text ||
            annotation.Target2D?.X != updatedAnnotation.Target2D?.X ||
            annotation.Target2D?.Y != updatedAnnotation.Target2D?.Y ||
            annotation.Target3D?.X != updatedAnnotation.Target3D?.X ||
            annotation.Target3D?.Y != updatedAnnotation.Target3D?.Y ||
            annotation.Target3D?.Z != updatedAnnotation.Target3D?.Z ||
            annotation.TargetBoxMin?.X != updatedAnnotation.TargetBoxMin?.X ||
            annotation.TargetBoxMin?.Y != updatedAnnotation.TargetBoxMin?.Y ||
            annotation.TargetBoxMin?.Z != updatedAnnotation.TargetBoxMin?.Z ||
            annotation.TargetBoxMax?.X != updatedAnnotation.TargetBoxMax?.X ||
            annotation.TargetBoxMax?.Y != updatedAnnotation.TargetBoxMax?.Y ||
            annotation.TargetBoxMax?.Z != updatedAnnotation.TargetBoxMax?.Z ||
            annotation.AssetClass?.Name != updatedAnnotation.AssetClass?.Name;

        return c;
    }

    private _deleteLabelUIOnly(label: ILabel, preserveSelection: boolean = false): void {
        const labelId = label.annotationData.ID;

        if (!preserveSelection) {
            this._selectedLabelIds.delete(labelId);
            if (this._isVolumeLabel(label)) {
                this._boxManager.setSelectedBox(labelId, false);
            }
        }

        this._preSelectCache.delete(labelId);
        this._preColorizeCache.delete(labelId);

        label.labelControl?.dispose();
        label.leaderLine?.dispose();
        label.attachedMesh?.dispose();
        label.lastMeasure = undefined;
    }

    private deleteLabel(label: ILabel) {
        this._deleteLabelUIOnly(label);
        this._boxManager.removeBox(label.annotationData.ID);
    }

    private precomputeAnnotationLayouts(container: BABYLONGUI.Container = this._guiTexture.rootContainer) {
        const textureSize = this._guiTexture.getSize();
        const context = this._guiTexture.getContext();
        const measure = new BABYLONGUI.Measure(0, 0, textureSize.width, textureSize.height);
        container._layout(measure, context);
    }

    private createAnnotation(annotation: IAnnotationDisplayItem, tagDisplayXml: string) {
        const xmlLoader = new XmlLoader2(annotation);
        xmlLoader.loadLayoutFromString(tagDisplayXml, null);

        const annControl: BABYLONGUI.Container = xmlLoader.getNodeById("root");
        this._captureBaseTextColors(annControl);

        const baseUrl = UrlUtilities.getBaseApplicationUrl();
        this._updateSourceUrls(annControl, baseUrl);

        this._guiTexture.addControl(annControl);

        let annotationPosition: BABYLON.Vector3 | undefined;


        if (annotation.TargetBoxMin) {
            annotationPosition = new BABYLON.Vector3(
                (annotation.TargetBoxMin.X + annotation.TargetBoxMax.X) / 2,
                (annotation.TargetBoxMin.Y + annotation.TargetBoxMax.Y) / 2,
                (annotation.TargetBoxMin.Z + annotation.TargetBoxMax.Z) / 2
            );

            const center = new BABYLON.Vector3(
                (annotation.TargetBoxMin.X + annotation.TargetBoxMax.X) / 2,
                (annotation.TargetBoxMin.Y + annotation.TargetBoxMax.Y) / 2,
                (annotation.TargetBoxMin.Z + annotation.TargetBoxMax.Z) / 2
            );
            const size = new BABYLON.Vector3(
                annotation.TargetBoxMax.X - annotation.TargetBoxMin.X,
                annotation.TargetBoxMax.Y - annotation.TargetBoxMin.Y,
                annotation.TargetBoxMax.Z - annotation.TargetBoxMin.Z
            );

            this._boxManager.upsertBox(annotation.ID, {
                realWorldPos: center,
                xDim: size.x, // depth -> world Z
                yDim: size.y, // height -> world Y
                zDim: size.z  // width  -> world X
            });

        } else if (annotation.Target3D) {
            annotationPosition = new BABYLON.Vector3(annotation.Target3D.X, annotation.Target3D.Y, annotation.Target3D.Z);
        }

        if (annotationPosition) {
            const index = this._labels.length;
            const attachmentSphere = BABYLON.MeshBuilder.CreateSphere('annSphere' + index, { diameter: 0.03 }, this._scene);
            attachmentSphere.position = annotationPosition;

            attachmentSphere.metadata = attachmentSphere.metadata ?? {};
            attachmentSphere.metadata._isAnnotationSphere = true;
            attachmentSphere.metadata.annotationId = annotation.ID;

            let lineData = { color: annControl.color };
            if (annotation.Target2D) {
                annControl.linkOffsetX = annotation.Target2D.X;
                annControl.linkOffsetY = annotation.Target2D.Y;
            } else {
                // Annotations without an offset (usually icons) do not need a connector line.
                if (annControl.linkOffsetXInPixels == 0 && annControl.linkOffsetYInPixels == 0) {
                    lineData = null;
                }
            }

            let leaderLine: BABYLONGUI.Line = null;

            if (lineData) {
                leaderLine = new BABYLONGUI.Line("annLeader_" + annotation.ID);
                leaderLine.color = lineData.color;
                leaderLine.lineWidth = 2;
                leaderLine.alpha = 1.0;
                leaderLine.isHitTestVisible = false;
                leaderLine.zIndex = 1000;
                this._guiTexture.addControl(leaderLine);
            }

            (annControl as any).originalColor = annControl.color;
            (annControl as any).originalBackground = annControl.background;
            // Swap alpha to only apply to background color
            annControl.color = this.hexToRgb(annControl.color, 1.0);
            annControl.background = this.hexToRgb(annControl.background, annControl.alpha);
            annControl.alpha = 1.0;
            (annControl as any)._annotationBaseColor = annControl.color;
            (annControl as any)._annotationBaseBackground = annControl.background;
            annControl.linkWithMesh(attachmentSphere);

            const annotationDataCopy = { ...annotation };
            const labelData: ILabel = {
                labelControl: annControl,
                attachedMesh: attachmentSphere,
                leaderLine,
                lineData,
                annotationData: annotationDataCopy
            };

            const isAssetTag =
                annotation.AnnotationType === AnnotationType.AssetTag ||
                annotation.AnnotationType === AnnotationType.AssetTagWithBox;

            const isAlwaysVisible =
                annotation.AnnotationType === AnnotationType.ObservationTag ||
                annotation.AnnotationType === AnnotationType.LinkAnnotation ||
                annotation.AnnotationType === AnnotationType.InformationAnnotation;

            const tagVisible = (isAssetTag && this._tagsVisible) || isAlwaysVisible;


            this.applyLabelVisibility(labelData, tagVisible);
            return labelData;
        }

        // If we got here, no Target3D/box — still return a label without sphere if your UI requires
        const dummy = BABYLON.MeshBuilder.CreateSphere('annSphere_unused_' + annotation.ID, { diameter: 0.001 }, this._scene);
        dummy.setEnabled(false);
        (annControl as any)._annotationBaseColor = annControl.color;
        (annControl as any)._annotationBaseBackground = annControl.background;
        const labelData: ILabel = {
            labelControl: annControl,
            attachedMesh: dummy,
            lineData: null,
            annotationData: { ...annotation }
        };

        const isAssetTag =
            annotation.AnnotationType === AnnotationType.AssetTag ||
            annotation.AnnotationType === AnnotationType.AssetTagWithBox;

        const isAlwaysVisible =
            annotation.AnnotationType === AnnotationType.ObservationTag ||
            annotation.AnnotationType === AnnotationType.LinkAnnotation ||
            annotation.AnnotationType === AnnotationType.InformationAnnotation;

        const tagVisible = (isAssetTag && this._tagsVisible) || isAlwaysVisible;

        this.applyLabelVisibility(labelData, tagVisible);
        return labelData;
    }

    selectAnnotation(annotation: string, isSelected: boolean): boolean {
        const label = this._labels.find(l => l.annotationData.ID === annotation);
        if (!label) return false;

        this.selectLabel(label, isSelected);
        return true;
    }

    private selectLabel(label: ILabel, isSelected: boolean): void {
        const labelId = label.annotationData.ID;

        if (isSelected) {
            if (!this._preSelectCache.has(labelId)) {
                const textMap = new Map<BABYLONGUI.TextBlock, string>();
                this._preSelectCache.set(labelId, {
                    color: label.labelControl.color,
                    background: label.labelControl.background,
                    textMap: textMap
                });
            }

            const cache = this._preSelectCache.get(labelId)!;

            label.labelControl.color = this.SelectedTagColor;
            label.labelControl.background = this.SelectedTagColor;

            this._setTextColorRecursive(label.labelControl, this.SelectedFontColor, cache.textMap);

            if (label.leaderLine) {
                label.leaderLine.color = this.SelectedTagColor;
            }

            this._selectedLabelIds.add(labelId);

            if (this._isVolumeLabel(label)) {
                this._boxManager.setSelectedBox(labelId, true);
            }

            this.precomputeAnnotationLayouts();
        } else {
            const selectCache = this._preSelectCache.get(labelId);
            const activeAppearance = this._activeAppearance.get(labelId);

            if (activeAppearance) {
                label.labelControl.color = activeAppearance.backgroundHex;
                label.labelControl.background = activeAppearance.backgroundHex;

                if (selectCache) {
                    this._restoreTextColors(selectCache.textMap);
                }
                this._setTextColorRecursive(label.labelControl, activeAppearance.textColor, new Map<BABYLONGUI.TextBlock, string>());

                if (label.leaderLine) {
                    label.leaderLine.color = activeAppearance.backgroundHex;
                }
            } else if (selectCache) {
                label.labelControl.color = selectCache.color;
                label.labelControl.background = selectCache.background;
                this._restoreTextColors(selectCache.textMap);

                if (label.leaderLine) {
                    label.leaderLine.color = label.labelControl.color;
                }
            }

            this._preSelectCache.delete(labelId);
            this._selectedLabelIds.delete(labelId);

            if (this._isVolumeLabel(label)) {
                this._boxManager.setSelectedBox(labelId, false);

                if (activeAppearance) {
                    this._boxManager.colorizeBox(labelId, activeAppearance.backgroundHex);
                } else {
                    this._boxManager.resetBoxColorization(labelId);
                }
            }

            if (this._selectedLabelIds.size !== 1) {
                this._boxManager.setPositioningMode('none');
            }
        }
    }

    private _isLabelSelected(label: ILabel): boolean {
        return this._selectedLabelIds.has(label.annotationData.ID);
    }

    private _isVolumeLabel(label: ILabel): boolean {
        return !!label.annotationData.TargetBoxMin;
    }

    private applyLabelVisibility(label: ILabel, visible: boolean): void {
        if (!label) return;
        // GUI
        if (label.labelControl) label.labelControl.isVisible = visible;

        // Linked 3D marker
        if (label.attachedMesh) label.attachedMesh.setEnabled(visible);

        // Optional explicit line (if you ever create BABYLON.LinesMesh)
        //if (label.line) label.line.setEnabled(visible);
        if (label.leaderLine) label.leaderLine.isVisible = visible;
        if (this._isBoxLabel(label)) {
            this._boxManager.setBoxVisibility(label.annotationData.ID, visible);
        }
    }

    colorizeAnnotations(annotationIds: string[], backgroundColor: number): void {

        var backgroundHex = this._rgbaNumberToHexRRGGBBAA(backgroundColor);
        const textColor = this._getReadableTextColor(backgroundHex);
        for (const annId of annotationIds) {
            const label = this._labels.find(l => l.annotationData.ID === annId);
            if (!label) continue;
            
            const preSelect = this._preSelectCache.get(annId);
            const textMap = this._isLabelSelected(label) ? (preSelect?.textMap ?? new Map<BABYLONGUI.TextBlock, string>()) : new Map<BABYLONGUI.TextBlock, string>();
            if (!this._preColorizeCache.has(annId)) {
                const labelControl = label.labelControl as any;
                this._preColorizeCache.set(annId, {
                    // Always restore to the XML-derived label appearance. Capturing the
                    // current onscreen color here can make a transient manage-mode gold
                    // override become the tag's new "default".
                    color: labelControl._annotationBaseColor
                        ?? (this._isLabelSelected(label) ? preSelect?.color : null)
                        ?? label.labelControl.color,
                    background: labelControl._annotationBaseBackground
                        ?? (this._isLabelSelected(label) ? preSelect?.background : null)
                        ?? label.labelControl.background,
                    textMap: textMap
                });
            }

            this._activeAppearance.set(annId, {
                backgroundHex: backgroundHex,
                textColor: textColor
            });

            label.labelControl.color = backgroundHex;
            label.labelControl.background = backgroundHex;

            if (label.leaderLine) {
                label.leaderLine.color = backgroundHex;
            }

            this._setTextColorRecursive(label.labelControl, textColor, textMap);

            if (this._isLabelSelected(label)) {
                this.selectLabel(label, true);
            }
            else if (this._isVolumeLabel(label)) {
                this._boxManager.colorizeBox(annId, backgroundHex);
            }
        }   
    }

    resetAllAnnotationColorization(): void {
        this._preColorizeCache.forEach((colors, annId) => {
            const label = this._labels.find(l => l.annotationData.ID === annId);
            if (!label) return;
            label.labelControl.color = colors.color;
            label.labelControl.background = colors.background;
            this._restoreTextColors(colors.textMap);

            if (label.leaderLine) {
                label.leaderLine.color = colors.color;
            }

            if (this._isLabelSelected(label)) {
                // Selection temporarily masks the asset-set appearance. Keep its
                // restore cache aligned with the newly restored base appearance so
                // deselecting cannot resurrect the previous asset-set override.
                const selectCache = this._preSelectCache.get(annId);
                if (selectCache) {
                    selectCache.color = colors.color;
                    selectCache.background = colors.background;
                    selectCache.textMap = new Map(colors.textMap);
                }
                this.selectLabel(label, true);
            }
            else if (this._isVolumeLabel(label)) {
                this._boxManager.resetBoxColorization(annId);
            }
        });
        this._preColorizeCache.clear();
        this._activeAppearance.clear();
    }
    _rgbaNumberToHexRRGGBBAA(color: number): string {
        const hex = (color >>> 0).toString(16).padStart(8, "0");
        return `#${hex}`;
    }
    _getReadableTextColor(background: string): string
    {
        // Convert hex to BYTE RGB components
        const babylonColor = BABYLON.Color4.FromHexString(background);
        const bg = { R: Math.round(babylonColor.r * 255), G: Math.round(babylonColor.g * 255), B: Math.round(babylonColor.b * 255) };

        // Calculate the perceptive luminance (Y) - human eye favors green.
        const luminance = (0.299 * bg.R + 0.587 * bg.G + 0.114 * bg.B) / 255;
    
        // If the luminance is high, the background is light → use black text.
        return luminance > 0.5 ? "#000000" : "#FFFFFF";
    }

    setTagsVisible(visible: boolean): void {
        this._tagsVisible = visible;
        for (const lbl of this._labels) {
            const isAssetTag =
                lbl.annotationData.AnnotationType === AnnotationType.AssetTag ||
                lbl.annotationData.AnnotationType === AnnotationType.AssetTagWithBox;

            const isAlwaysVisible =
                lbl.annotationData.AnnotationType === AnnotationType.ObservationTag ||
                lbl.annotationData.AnnotationType === AnnotationType.LinkAnnotation ||
                lbl.annotationData.AnnotationType === AnnotationType.InformationAnnotation;

            const tagVisible = (isAssetTag && this._tagsVisible) || isAlwaysVisible;

            this.applyLabelVisibility(lbl, tagVisible);
        }
    }
    setShowOccludedTags(show: boolean): void {
        this._showOccludedTags = show;
    }

    containsAnnotation(annotation: string): boolean {
        return this._labels.some(label => label.annotationData.ID === annotation);
    }

    pickAnnotationByClick(pointerX: number, pointerY: number): string | null {

        const isAlwaysVisibleType = (t: AnnotationType): boolean =>
            t === AnnotationType.ObservationTag ||
            t === AnnotationType.LinkAnnotation ||
            t === AnnotationType.InformationAnnotation;

        const isAssetTagType = (t: AnnotationType): boolean =>
            t === AnnotationType.AssetTag ||
            t === AnnotationType.AssetTagWithBox;

        for (const label of this._labels) {
            if (!label?.labelControl) continue;
            if (!label.labelControl.isVisible) continue;
            if (!label.labelControl.contains(pointerX, pointerY)) continue;

            const type = label.annotationData?.AnnotationType;

            if (!this._tagsVisible && isAssetTagType(type)) continue;

            if (this._tagsVisible || isAlwaysVisibleType(type)) {
                return label.annotationData.ID;
            }
        }

        const pick = this._scene.pick(
            pointerX,
            pointerY,
            (m) =>
                !!m &&
                m.isEnabled() &&
                m.isVisible &&
                m.name === BoxManager.BOX_NAME &&
                // make sure it's one of *our* labels
                this.containsAnnotation(m.id),
            false,
            this._scene.activeCamera
        );

        if (pick?.hit && pick.pickedMesh) {
            return pick.pickedMesh.id; // in your BoxManager, this is the annotation id
        }

        return null;
    }

    private hexToRgb(hex: string, alpha: number) {
        var hexRGB = hex.replace(/^#/, '');
        let bigint = parseInt(hexRGB, 16);
        let r = (bigint >> 16) & 255;
        let g = (bigint >> 8) & 255;
        let b = bigint & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    private _updateSourceUrls(control: BABYLONGUI.Control, baseUrl: string) {
        if (control instanceof BABYLONGUI.Image && control.source) {
            if (!control.source.startsWith('http://') && !control.source.startsWith('https://')) {
                control.source = `${baseUrl}/${control.source}`;
            }
        }
        if ('children' in control) {
            for (const child of (control as any).children) {
                this._updateSourceUrls(child, baseUrl);
            }
        }
    }
    private _project2DPointTo3D = (x: number, y: number) => {
        let screenCoords = new BABYLON.Vector3(x, y, 0);
        let worldCoords = BABYLON.Vector3.Unproject(screenCoords,
            this._scene.getEngine().getRenderWidth(),
            this._scene.getEngine().getRenderHeight(),
            BABYLON.Matrix.Identity(),
            this._scene.activeCamera.getViewMatrix(),
            this._scene.activeCamera.getProjectionMatrix());

        return worldCoords;
    }
    private _onAfterCameraRender(camera: BABYLON.Camera) {
        const scene = this._scene;
        const engine = scene.getEngine();
        const cam = scene.activeCamera;
        if (!cam || !this._guiTexture) return;

        const renderW = engine.getRenderWidth();
        const renderH = engine.getRenderHeight();
        const vp = cam.viewport.toGlobal(renderW, renderH);

        for (const labelData of this._labels) {
            const line = labelData.leaderLine;
            if (!line) continue;

            
            // Respect visibility (same behavior as old)
            const visible = !!labelData.labelControl?.isVisible;
            line.isVisible = visible;
            if (!visible) continue;

            // 1) 3D anchor -> screen pixel (viewport pixel coords)
            const p3 = labelData.attachedMesh.getAbsolutePosition();
            const projected = BABYLON.Vector3.Project(
                p3,
                BABYLON.Matrix.Identity(),
                scene.getTransformMatrix(),
                vp
            );

            // If behind camera / offscreen, hide line
            if (projected.z < 0 || projected.z > 1) {
                line.isVisible = false;
                continue;
            }

            const rect = this._getStableMeasure(labelData);
            if (!rect) {
                continue;
            }

            const labelGuiX = rect.left + rect.width * 0.5;
            const labelGuiY = rect.top;

            // GUI pixels -> viewport pixels
            const guiSize = this._guiTexture.getSize();
            const screenX = vp.x + (labelGuiX / guiSize.width) * vp.width;
            const screenY = vp.y + (labelGuiY / guiSize.height) * vp.height;

            // 3) Feed GUI.Line endpoints.
            // GUI.Line uses coordinates in GUI space, not viewport space.
            // So we convert both endpoints into GUI pixel coordinates (same space as _currentMeasure).

            // Convert projected viewport pixel -> GUI pixel
            const anchorGuiX = ((projected.x - vp.x) / vp.width) * guiSize.width;
            const anchorGuiY = ((projected.y - vp.y) / vp.height) * guiSize.height;

            line.x1 = anchorGuiX;
            line.y1 = anchorGuiY;
            line.x2 = labelGuiX;
            line.y2 = labelGuiY;

            // Keep color in sync if you recolor the label
            //if (labelData.lineData?.color) line.color = labelData.lineData.color;
        }
    }

    public calculateOccludedLabelsTimeCapped(timeBudgetMs: number): void {
        const labels = this._labels;

        if (!labels || labels.length === 0) {
            this._occlCursor = 0;
            return;
        }
        if (this._occlCursor >= labels.length) this._occlCursor = 0;

        const start = BABYLON.Tools.Now;
        const scene = this._scene;
        const camera = scene.activeCamera as ExamineCamera;

        const baseOrigin = camera.position.clone();
        const minZ = camera.minZ;

        const engine = scene.getEngine();
        const vp = camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight());
        const planes = BABYLON.Frustum.GetPlanes(scene.getTransformMatrix());
        const camForward = camera.target.subtract(camera.position).normalize();

        // Collect active clip planes
        const clipPlanes: BABYLON.Plane[] = [];
        if (scene.clipPlane) clipPlanes.push(scene.clipPlane);
        if (scene.clipPlane2) clipPlanes.push(scene.clipPlane2);
        if (scene.clipPlane3) clipPlanes.push(scene.clipPlane3);
        if (scene.clipPlane4) clipPlanes.push(scene.clipPlane4);
        if (scene.clipPlane5) clipPlanes.push(scene.clipPlane5);
        if (scene.clipPlane6) clipPlanes.push(scene.clipPlane6);

        const _passesClipPlanes = (pointWS: BABYLON.Vector3): boolean => {
            if (!clipPlanes.length) return true;
            for (const p of clipPlanes) {
                // With clampSceneToBoundingBox planes, signedDistance > 0 = outside (clipped)
                if (p.signedDistanceTo(pointWS) > 0) return false;
            }
            return true;
        };

        // helper: corners of a mesh's world-space bbox (8 points)
        const _getBBoxCorners = (mesh: BABYLON.AbstractMesh): BABYLON.Vector3[] => {
            return mesh.getBoundingInfo().boundingBox.vectorsWorld;
        };

        const _isOpaque = (m: BABYLON.AbstractMesh): boolean => {
            // mesh-level visibility multiplier
            if (typeof m.visibility === "number" && m.visibility < 0.999) return false;

            const checkMat = (mat?: BABYLON.Material | null): boolean => {
                if (!mat) return true; // no material -> assume opaque

                // MultiMaterial: ALL sub-materials must be opaque
                if (mat instanceof BABYLON.MultiMaterial) {
                    const subs = mat.subMaterials ?? [];
                    for (const sm of subs) if (!checkMat(sm)) return false;
                    return true;
                }

                // Base alpha
                if ((mat.alpha ?? 1) < 0.999) return false;

                // StandardMaterial specifics
                if (mat instanceof BABYLON.StandardMaterial) {
                    if (mat.useAlphaFromDiffuseTexture) return false; // per-pixel alpha
                    return true;
                }

                // PBRMaterial specifics
                if (mat instanceof BABYLON.PBRMaterial) {
                    const tm = mat.transparencyMode;
                    if (
                        tm === BABYLON.PBRMaterial.PBRMATERIAL_ALPHABLEND ||
                        tm === BABYLON.PBRMaterial.PBRMATERIAL_ALPHATESTANDBLEND
                    ) return false;

                    if (mat.alpha && mat.alpha < 0.999) return false;
                    return true;
                }

                // Fallback for other material types: rely on mat.alpha only
                return (mat.alpha ?? 1) >= 0.999;
            };

            return checkMat(m.material ?? null);
        };

        // helper: is a screen point inside the viewport
        const _isOnScreen = (p: BABYLON.Vector3): boolean => {
            const sp = BABYLON.Vector3.Project(p, BABYLON.Matrix.Identity(), scene.getTransformMatrix(), vp);
            return (sp.z >= 0 && sp.z <= 1 && sp.x >= 0 && sp.y >= 0 && sp.x <= vp.width && sp.y <= vp.height);
        };

        const _isAlwaysVisibleType = (t: AnnotationType): boolean =>
            t === AnnotationType.ObservationTag ||
            t === AnnotationType.LinkAnnotation ||
            t === AnnotationType.InformationAnnotation;

        const _isAssetTagType = (t: AnnotationType): boolean =>
            t === AnnotationType.AssetTag ||
            t === AnnotationType.AssetTagWithBox;

        while (this._occlCursor < labels.length) {
            const label = labels[this._occlCursor];
            const type = label.annotationData.AnnotationType;

            // If tags are globally hidden, hide ONLY asset-tag labels.
            // Always-visible types still need occlusion testing.
            if (!this._tagsVisible && _isAssetTagType(type)) {
                label.labelControl.alpha = 0.0;
                label.labelControl.isVisible = false;
                this._occlCursor++;
                if (BABYLON.Tools.Now - start >= timeBudgetMs) return;
                continue;
            }

            const boxMesh = this._boxManager.getBoxMesh(label.annotationData.ID);
            const attached = label?.attachedMesh as BABYLON.AbstractMesh | undefined;

            // if neither exists, we can't sensibly test—hide
            if (!boxMesh && !attached) {
                label.labelControl.alpha = 0.0;
                label.labelControl.isVisible = false;
                this._occlCursor++;
                if (BABYLON.Tools.Now - start >= timeBudgetMs) return;
                continue;
            }

            let samplePoints: BABYLON.Vector3[] = [];
            let primaryForFacing: BABYLON.Vector3;

            if (boxMesh && boxMesh.isEnabled() && boxMesh.isVisible) {
                const bi = boxMesh.getBoundingInfo();

                // Frustum test: full bbox
                if (!bi.isInFrustum(planes)) {
                    label.labelControl.alpha = 0.0;
                    label.labelControl.isVisible = false;
                    this._occlCursor++;
                    if (BABYLON.Tools.Now - start >= timeBudgetMs) return;
                    continue;
                }

                primaryForFacing = bi.boundingBox.centerWorld;

                // Clip-plane test: center of box
                if (!_passesClipPlanes(primaryForFacing)) {
                    label.labelControl.alpha = 0.0;
                    label.labelControl.isVisible = false;
                    this._occlCursor++;
                    if (BABYLON.Tools.Now - start >= timeBudgetMs) return;
                    continue;
                }

                const toBox = primaryForFacing.subtract(camera.position);
                if (BABYLON.Vector3.Dot(camForward, toBox) <= 0) {
                    label.labelControl.alpha = 0.0;
                    label.labelControl.isVisible = false;
                    this._occlCursor++;
                    if (BABYLON.Tools.Now - start >= timeBudgetMs) return;
                    continue;
                }

                // Use corners for sampling/ray tests
                samplePoints = _getBBoxCorners(boxMesh);
            } else {
                // No box—fall back to attached point
                const p = attached!.getAbsolutePosition();
                primaryForFacing = p;

                // Clip-plane test: label point
                if (!_passesClipPlanes(primaryForFacing)) {
                    label.labelControl.alpha = 0.0;
                    label.labelControl.isVisible = false;
                    this._occlCursor++;
                    if (BABYLON.Tools.Now - start >= timeBudgetMs) return;
                    continue;
                }

                const toPt = p.subtract(camera.position);
                if (BABYLON.Vector3.Dot(camForward, toPt) <= 0) {
                    label.labelControl.alpha = 0.0;
                    label.labelControl.isVisible = false;
                    this._occlCursor++;
                    if (BABYLON.Tools.Now - start >= timeBudgetMs) return;
                    continue;
                }

                samplePoints = [p];
            }

            // Screen-space check: any sample point on-screen?
            let anyOnScreen = false;
            for (let i = 0; i < samplePoints.length; i++) {
                if (_isOnScreen(samplePoints[i])) { anyOnScreen = true; break; }
            }
            if (!anyOnScreen) {
                label.labelControl.alpha = 0.0;
                label.labelControl.isVisible = false;
                this._occlCursor++;
                if (BABYLON.Tools.Now - start >= timeBudgetMs) return;
                continue;
            }

            // 3) Ray occlusion: label is visible if ANY sample point is unobstructed
            const startOffset = Math.max(minZ, 0.05);
            let isVisible = false;

            for (let i = 0; i < samplePoints.length; i++) {
                const p = samplePoints[i];
                const dir = p.subtract(baseOrigin);
                const len = dir.length();

                if (len < 1e-4) {
                    isVisible = true;
                    break;
                }

                const dirN = dir.scale(1 / len);
                const originBiased = baseOrigin.add(dirN.scale(startOffset));
                const dist = BABYLON.Vector3.Distance(originBiased, p);
                if (dist < 1e-4) {
                    isVisible = true;
                    break;
                }

                const ray = new BABYLON.Ray(originBiased, dirN, dist - 0.03);
                const pick = scene.pickWithRay(
                    ray,
                    (m) =>
                        m !== boxMesh &&
                        m.isEnabled() &&
                        m.isVisible &&
                        (m as any).getTotalIndices?.() > 0 &&
                        m.name !== "annBox" &&
                        _isOpaque(m as BABYLON.AbstractMesh),
                    true
                );

                // Only count as occluded if the hit is inside clip planes
                if (!pick.hit || !pick.pickedPoint) {
                    isVisible = true;
                    break;
                }

                if (clipPlanes.length > 0 && !_passesClipPlanes(pick.pickedPoint)) {
                    // Occluder is outside clipped volume → ignore this hit
                    isVisible = true;
                    break;
                }

                // Otherwise: valid occluder for this sample, try next sample point
            }

            if (isVisible) {
                label.labelControl.alpha = 1.0;
                label.labelControl.isVisible = true;
            } else {
                const isAlwaysVisibleType = _isAlwaysVisibleType(type);

                if (!isAlwaysVisibleType) {
                    label.labelControl.alpha = this.OccludedTagAlpha;
                }

                label.labelControl.isVisible = !isAlwaysVisibleType && this._showOccludedTags;
            }

            this._occlCursor++;
            if (BABYLON.Tools.Now - start >= timeBudgetMs) return;
        }

        this._occlCursor = 0;
    }


}

export default LabelManager;
